import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { LANGUAGES } from './languages';
import { applyMetadata } from './metadata';
import { translations } from './translations';

const LanguageContext = createContext(null);
const STORAGE_KEY = 'apc-lang';

function localize(language) {
    const local = translations[language] || translations.en;
    if (language === 'en') return local;
    return new Proxy(local, {
        get(target, key) {
            const value = target[key];
            return value == null || value === '' ? translations.en[key] : value;
        },
    });
}

function readLanguage() {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return LANGUAGES.some((item) => item.id === saved) ? saved : 'en';
}

export function LanguageProvider({ children }) {
    const [language, setLanguage] = useState(readLanguage);

    useEffect(() => {
        const current = LANGUAGES.find((item) => item.id === language) || LANGUAGES[0];
        const copy = localize(language);
        applyMetadata({
            title: copy.metaTitle,
            description: copy.metaDescription,
            htmlLang: current.htmlLang,
            ogLocale: current.ogLocale,
        });
        window.localStorage.setItem(STORAGE_KEY, language);
    }, [language]);

    const value = useMemo(
        () => ({
            language,
            setLanguage,
            t: localize(language),
        }),
        [language]
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
    return useContext(LanguageContext);
}
